import { useEffect } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { FieldValues, UseFormRegister, UseFormWatch } from "react-hook-form";

type AutocompleteFieldOption = string | { label: string; id: string };

interface AutocompleteFieldProps {
  register?: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  onChange: (value: AutocompleteFieldOption[]) => void;
  options: AutocompleteFieldOption[];
  label: string;
  required?: boolean | string;
  id: string;
  sx?: any;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  getOptionLabel?: (option: any) => string;
}

export const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  options,
  onChange,
  label,
  register,
  id,
  required,
  watch,
  sx,
  multiple = false,
  disabled = false,
  error = false,
  loading = false,
  helperText = "",
  getOptionLabel = (option) => option.label,
}) => {
  const value = watch(id);

  useEffect(() => {
    if (register) {
      register(id, { required });
    }
  }, []);

  const extended_options = multiple === true ? getOptionsWithSelectAll() : options;

  function getCustomOptionLabels(option: any) {
    if (typeof option === "string") return option;
    if (option?.id === "select-or-deselect-all-options") return option.label;

    return getOptionLabel(option);
  }

  function getOptionsWithSelectAll() {
    if (options.length === 0) return options;
    if (multiple && !value) {
      console.error("NO_VALUE_FOR_DROPDOWN_WITH_ID", id);
      return [];
    }
    if (value.length === options.length) {
      if (typeof options?.[0] === "string") {
        return [ "Deselect All", ...options];
      }
      return [{ id: "select-or-deselect-all-options", label: "Deselect All" }, ...options];
    }
    if (typeof options?.[0] === "string") {
      return ["Select All", ...options];
    }
    return [{ id: "select-or-deselect-all-options", label: "Select All" }, ...options];
  }

  return (
    <Autocomplete
      size="small"
      multiple={multiple}
      disableCloseOnSelect={multiple}
      limitTags={2}
      options={extended_options}
      value={value ?? (multiple === true ? [] : "")}
      getOptionLabel={getCustomOptionLabels}
      onChange={(event, value) => {
        if(typeof value?.[0] === "string") {
          switch (true) {
            case !Array.isArray(value):
              return onChange(value);
            case value.includes("Select All"):
              return onChange(options);
            case value.includes("Deselect All"):
              return onChange([]);
            default:
              return onChange(value);
          }
        } else {
          switch (true) {
            case !Array.isArray(value):
              return onChange(value);
            case value.label?.includes("Select All"):
              return onChange(options);
            case value.label?.includes("Deselect All"):
              return onChange([]);
            default:
              return onChange(value);
          }
        }
      }}
      sx={sx}
      loading={loading}
      disabled={disabled}
      isOptionEqualToValue={(option, value) =>
        typeof option === "object" && typeof value === "object" ? option.id === value.id : option === value
      }
      renderTags={(value, getTagProps) => {
        return (
          <div>
            <Chip
              size="small"
              variant="outlined"
              label={typeof value[0] === "string" ? value[0] : value[0]?.label}
              {...getTagProps}
            />
            {value.length > 1 && <Chip size="small" label={`+${value.length - 1}`} />}
          </div>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={label} error={error} helperText={helperText} />
      )}
    />
  );
};
export default AutocompleteField;
